import { ref, computed, watch, onUnmounted, unref } from "vue";
import store from "@/store";
import { checkOrgObjectNameAvailability } from "@/api/orgObjectNameAPI";
import { NAME_IN_USE, nameConflictMessage } from "@components/constants/orgObjectMessages";

const DEBOUNCE_MS = 300;

// Config values may be plain values, refs, or getters so a dialog reused across rows
// always checks against the row being renamed.
const resolve = (value) => (typeof value === "function" ? value() : unref(value));

/** True for a 409 whose body code ends in `_DUPLICATE_NAME` (the shared uniqueness contract). */
export function isDuplicateNameError(err) {
  const status = err && err.response && err.response.status;
  const code = err && err.response && err.response.data && err.response.data.code;
  return status === 409 && typeof code === "string" && /_DUPLICATE_NAME$/.test(code);
}

/**
 * Shared inline name-uniqueness validation for org objects (Role, Responsibility,
 * Department, Job Family, Job Family Level, Job Category).
 *
 * ER-11899 / MDP-608: the backend `name-availability` endpoint is the single source
 * of truth — no client-side case folding, no partial-list pre-checks. The composable
 * debounces the availability GET (300 ms) and only ever reports one copy string
 * (`NAME_IN_USE`). Positions are never checked.
 *
 * @param {{ type: string, accountId?: string|number|Function, excludeId?: string|number|Function,
 *           jobFamilyId?: string|number|Function }} config  `accountId` defaults to the
 *           current account in localStorage.
 */
export function useOrgObjectNameValidation({ type, accountId, excludeId, jobFamilyId } = {}) {
  const name = ref("");
  const trimmedName = computed(() => name.value.trim());
  const error = ref("");
  const checking = ref(false);

  // Fail-closed: until the `idea-MDP-608` flag resolves true the composable is a
  // no-op, preserving the legacy (no inline check) behaviour.
  const flagEnabled = Boolean(store && store.getters && store.getters["featureFlags/ideaMDP608"]);

  let debounceTimer = null;
  let requestSeq = 0;

  const isValid = computed(() => !checking.value && error.value === "");

  async function runAvailabilityCheck(value) {
    const trimmed = value.trim();
    if (!trimmed || type === "position" || !flagEnabled) {
      error.value = "";
      checking.value = false;
      return;
    }

    checking.value = true;
    const seq = ++requestSeq;
    try {
      // The availability endpoint answers 200 with `{ available, normalized_name,
      // code }`; `code` is set only when a live object already holds the name.
      const response = await checkOrgObjectNameAvailability({
        accountId: resolve(accountId) ?? localStorage.getItem("currentAccountID"),
        type,
        name: trimmed,
        excludeId: resolve(excludeId),
        jobFamilyId: resolve(jobFamilyId),
      });
      if (seq === requestSeq) {
        const result = (response && response.data) || {};
        error.value = result.available === false && result.code ? NAME_IN_USE : "";
      }
    } catch (err) {
      if (seq !== requestSeq) return;
      // Defensive: a 409 for this call still means the name is taken.
      error.value = err && err.response && err.response.status === 409 ? NAME_IN_USE : "";
    } finally {
      if (seq === requestSeq) checking.value = false;
    }
  }

  function scheduleCheck(value) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runAvailabilityCheck(value), DEBOUNCE_MS);
  }

  // `flush: "sync"` schedules the debounce immediately on input (and keeps the
  // watcher deterministic when the composable is used outside a component setup).
  watch(
    name,
    (value) => {
      if (!flagEnabled || type === "position") {
        error.value = "";
        checking.value = false;
        return;
      }
      checking.value = false;
      scheduleCheck(value);
    },
    { flush: "sync" }
  );

  onUnmounted(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
  });

  /**
   * Map a submit-time error to the inline name-in-use message. Returns true when
   * the caller should keep the dialog open (409 + a *_DUPLICATE_NAME code),
   * otherwise false (the error is unrelated and passes through to the caller).
   */
  function validateOnSubmitError(err) {
    const message = nameConflictMessage(err);
    if (message) error.value = message;
    return Boolean(message);
  }

  return {
    name,
    trimmedName,
    error,
    checking,
    isValid,
    validateOnSubmitError,
  };
}
